import { addDays, isAfter, isSameDay, subBusinessDays } from "date-fns";
import DOMPurify from "dompurify";
import { SurveyDispatchReminder } from "../types/survey";

export const SURVEY_INVITE_TOKEN_KEY = "survey_invite_token"; // defined by the backend
const LOCAL_STORAGE_TOKEN_KEY = "survey_browser_fingerprint";

export const getLocalStorageTokenKey = (inviteToken: string | null) => {
  if (!inviteToken) {
    return "";
  }

  return `${LOCAL_STORAGE_TOKEN_KEY}_${inviteToken}`;
};

export const getBrowserFingerprint = (inviteToken: string | null) => {
  const tokenKey = getLocalStorageTokenKey(inviteToken);
  return localStorage.getItem(tokenKey);
};

export const setBrowserFingerprint = (inviteToken: string | null) => {
  const tokenKey = getLocalStorageTokenKey(inviteToken);
  if (!tokenKey) {
    return;
  }

  const fingerprint = generateBrowserFingerprint();
  localStorage.setItem(tokenKey, fingerprint);
};

export const hasSocialMediaReferrer = () => {
  const referrer = document.referrer;
  if (!referrer) {
    return false;
  }

  const socialMediaDomains = [
    "twitter.com",
    "x.com",
    "facebook.com",
    "instagram.com",
    "reddit.com",
    "linkedin.com",
    "pinterest.com",
    "t.co", // Twitter's URL shortener
  ];

  // Check if the referrer URL contains any of the social media domains
  return socialMediaDomains.some((domain) => referrer.includes(domain));
};

export const generateBrowserFingerprint = () => {
  const userAgent = navigator.userAgent;
  const screenResolution = `${window.screen.width}x${window.screen.height}`;
  const colorDepth = window.screen.colorDepth;
  const timeZoneOffset = new Date().getTimezoneOffset();

  // Combine all the collected information and hash it
  const fingerprintString = JSON.stringify({
    userAgent,
    screenResolution,
    colorDepth,
    timeZoneOffset,
  });

  // hash the fingerprintString to make it anonymous
  const hashedFingerprint = hash(fingerprintString);

  return hashedFingerprint.toString();
};

const hash = (str: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

export const sortReminders = (reminders: SurveyDispatchReminder[]) => {
  const finalReminders = reminders.filter((reminder) => reminder.isFinal);
  const sortedReminders = reminders
    .filter((reminder) => !reminder.isFinal)
    .sort((a, b) => {
      if (a.date && b.date) {
        return a.date.getTime() - b.date.getTime();
      }
      return 0;
    });
  return [...sortedReminders, ...finalReminders];
};

export const hasDuplicateReminders = (reminders: SurveyDispatchReminder[]) => {
  const uniqueDates = new Set(
    reminders.map((reminder) => reminder.date?.toDateString())
  );
  return uniqueDates.size !== reminders.length;
};

export const getReminderMinDate = (
  launchDate: Date | undefined,
  reminders: SurveyDispatchReminder[],
  reminder: SurveyDispatchReminder,
  today?: Date | undefined
) => {
  today = today || new Date(Date.now());
  today.setHours(0, 0, 0, 0);

  // use the day after the launch date if it's in the future, otherwise use today
  const baseMinDate =
    (launchDate && isSameDay(launchDate, today)) ||
    (launchDate && isAfter(launchDate, today))
      ? addDays(launchDate, 1)
      : today;

  // If it's a final reminder, it can't be before the last scheduled regular reminder
  if (reminder.isFinal) {
    const lastRegularReminder = getLastScheduledRegularReminder(reminders);
    if (lastRegularReminder?.date) {
      return isAfter(lastRegularReminder.date, baseMinDate)
        ? addDays(lastRegularReminder.date, 1)
        : baseMinDate;
    }
  }
  return baseMinDate;
};

export const getReminderMaxDate = (
  dueDate: Date | undefined,
  reminder: SurveyDispatchReminder,
  finalReminder?: SurveyDispatchReminder
) => {
  if (!dueDate) return undefined;

  if (reminder.isFinal) {
    return dueDate;
  }

  // Regular reminders can't be scheduled after an active final reminder
  if (finalReminder) {
    return finalReminder.date;
  }

  return dueDate;
};

const getLastScheduledRegularReminder = (
  reminders: SurveyDispatchReminder[]
) => {
  return reminders
    .filter((r) => !r.isFinal && r.date)
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))[0];
};

export const getLastBusinessDayBeforeDueDate = (dueDate: Date) => {
  return subBusinessDays(dueDate, 1);
};

export const sanitizeHTML = (html: string) => {
  const sanitizedHTML = DOMPurify.sanitize(html || "");

  // since target="_blank" is sanitized away, we need to add it back,
  //  so we search for rel="noreferrer" and add target="_blank"
  return sanitizedHTML.replace(
    /rel="noreferrer"/g,
    'rel="noreferrer" target="_blank"'
  );
};
